// Whether this phone has each mode, and the requests only a phone with it makes.
//
// ONE SWITCH PER OPT-IN MODE, AND IT IS OFF UNTIL SOMEBODY TURNS IT ON. Late
// Again was a train app for a year before it knew a tram existed, and the people
// who use it for trains must see no difference at all: not a row, not a section,
// not a request. So everything mode-shaped in the app asks `modeOn` first, and
// for an install that has never said yes the answer is no.
//
// Three ways to say yes, all of them a person's own act:
//
//   * the welcome's "Any <mode>?" step, by choosing a route;
//   * the one-time "You can add <mode> now" card on Your lines;
//   * the mode's row in Settings.
//
// Saying no is the same switch the other way, and it takes the mode's routes
// off the watched list with it — a route nobody can see must not be able to wake
// anybody (see `setModeOn` in app-state.js).
//
// SHARED WITH EVERY CITY, like the quiet windows: it is about the person. A
// mode only exists in the cities that declare it (src/modes.js), so elsewhere it
// simply has nothing to show.
//
// THE KEYS ARE THE MODE'S OWN, DECLARED IN THE REGISTRY. Melbourne's trams keep
// the keys they have always written (`lateagain:trams:v1`, …), so an install
// that already has trams on, a starred stop or an answered intro card is read
// exactly as before and nothing is migrated.

import { ref } from 'vue';
import { prefs } from './core/storage.js';
import { API_BASE } from './core/native.js';
import { askPatiently, busyError } from './busy.js';
import { couldntReach } from './source-copy.js';
import { allModes, capital } from './modes.js';
import { modeKey } from './mode-data.js';

const readIds = (key) => {
  const raw = prefs[key];
  return Array.isArray(raw) ? raw.map(Number).filter((n) => Number.isInteger(n) && n > 0) : [];
};

const slots = new Map();

/** One mode's refs, made the first time anything asks, from the stored values. */
function slot(mode) {
  const key = modeKey(mode);
  if (!slots.has(key)) {
    slots.set(key, {
      on: ref(prefs[mode.prefs.on] === true),
      intro: ref(prefs[mode.prefs.intro] === true),
      stars: ref(readIds(mode.prefs.stars)),
      opens: ref(Number(prefs[mode.prefs.opens]) > 0 ? Number(prefs[mode.prefs.opens]) : null),
    });
  }
  return slots.get(key);
}

// ── The switch ───────────────────────────────────────────────────────────────

/** Has this phone turned the mode on? Reactive. A mode that is not opt-in is always on. */
export const modeOn = (mode) => (mode.optIn ? slot(mode).on.value : true);

/** Write the switch. The watched routes are app-state's to tidy, not this file's. */
export function writeModeOn(mode, on) {
  const s = slot(mode);
  s.on.value = Boolean(on);
  prefs[mode.prefs.on] = s.on.value;
}

/**
 * Has the "You can add <mode> now" card been answered, either way?
 *
 * ONCE. A card that came back after "Not now" would be a nag on the screen
 * people open at 6:40 to find out whether their train is running, and the
 * Settings row is there for anybody who changes their mind.
 */
export const modeIntroSeen = (mode) => slot(mode).intro.value;

export function markModeIntroSeen(mode) {
  writeModeIntroSeen(mode, true);
}

/** Write the answer either way. The card marks it seen; tests and a reset write false. */
export function writeModeIntroSeen(mode, seen) {
  slot(mode).intro.value = Boolean(seen);
  prefs[mode.prefs.intro] = Boolean(seen);
}

/**
 * Should the card be offered? Only in the mode's city, only to an install with
 * the mode off, and only to somebody who already watches a train line — a person
 * part-way through the welcome is about to be asked the same question by the
 * welcome itself, and a brand-new install has nothing on the ledger for the card
 * to sit under.
 */
export function offersModeIntro({ mode, city, on, seen, watchedCount }) {
  return Boolean(mode?.optIn) && mode.city === city && !on && !seen && Number(watchedCount) > 0;
}

// ── The stops somebody keeps ─────────────────────────────────────────────────
//
// A STAR ON A MODE'S STOP, KEPT APART FROM THE STATIONS'. The starred stations
// are store rows the widget, the watch and four screens read as stations and look
// up by stop id in the train bundle; a tram stop's id can equal a station's and
// means something else, so putting one in that list would show the wrong stop on
// a lock screen. Each mode's list lives here, in preferences, and the sheet shows
// them together.
//
// Nothing here is cleared when a mode is turned off: the stops are kept, hidden
// with everything else of that mode, and are there again if it comes back.

/** The starred stops of a mode, by stop id, in the order they were starred. Reactive. */
export const modeStars = (mode) => slot(mode).stars.value;

export const isModeStarred = (mode, stopId) => slot(mode).stars.value.includes(Number(stopId));

/** Star or unstar a stop. Returns whether it is now starred. */
export function toggleModeStar(mode, stopId) {
  const s = slot(mode);
  const id = Number(stopId);
  if (!(id > 0)) return false;
  const on = !s.stars.value.includes(id);
  s.stars.value = on ? [...s.stars.value, id] : s.stars.value.filter((x) => x !== id);
  prefs[mode.prefs.stars] = s.stars.value;
  if (!on && s.opens.value === id) setModeOpens(mode, null);
  return on;
}

/**
 * The stop the board opens on, or null for "the default station". Reactive.
 *
 * ONE DEFAULT, ANY KIND. Making a mode's stop the default clears nothing about
 * the default station — the widget still reads that — but the board opens on the
 * stop until a station is made the default again, which is the sheet's own MAKE
 * DEFAULT on a station row. Two modes cannot both hold it: setting one clears
 * the others.
 */
export const modeOpens = (mode) => slot(mode).opens.value;

export function setModeOpens(mode, stopId) {
  const id = Number(stopId);
  const s = slot(mode);
  s.opens.value = id > 0 ? id : null;
  prefs[mode.prefs.opens] = s.opens.value;
  if (s.opens.value) {
    for (const other of allModes()) {
      if (other === mode || other.source !== 'adapter' || modeOpens(other) == null) continue;
      slot(other).opens.value = null;
      prefs[other.prefs.opens] = null;
    }
  }
}

/**
 * Hand the opening back to the default STATION: no mode's stop is opened on.
 *
 * Called by whatever changes which station is the default — the sheet's OPENS
 * HERE, but also a first star, or unstarring the default so the next one takes
 * over. Otherwise the board keeps opening on a tram stop that nothing on screen
 * says is still the choice.
 */
export function clearModeOpens() {
  for (const mode of allModes()) {
    if (mode.source === 'adapter' && modeOpens(mode) != null) setModeOpens(mode, null);
  }
}

/** Which of these modes' stops the board opens on: `{ mode, stopId }` or null. Reactive. */
export function openedStop(modes) {
  for (const mode of modes) {
    const stopId = modeOpens(mode);
    if (stopId) return { mode, stopId };
  }
  return null;
}

// ── The requests ─────────────────────────────────────────────────────────────

/** GET one of the mode's routes, with one patient retry if the server says busy. */
async function ask(mode, path, { timeout = 10_000, signal = null } = {}) {
  const answer = await askPatiently(async () => {
    try {
      const own = AbortSignal.timeout(timeout);
      const res = await fetch(`${API_BASE}${mode.api}${path}`, { signal: signal ? AbortSignal.any([own, signal]) : own });
      const body = await res.json().catch(() => null);
      return { ok: res.ok, body, status: res.status };
    } catch {
      return { ok: false, body: null, status: 0 };
    }
  });
  if (answer.ok && answer.body?.configured !== false) return answer.body;
  // The city's own source name, as the train copy says it (src/source-copy.js).
  throw answer.busy ? busyError(answer) : new Error(`${couldntReach()} for the ${mode.labels.many}.`);
}

/**
 * Every notice in force for the mode, in the train feed's row shape. Asked only
 * while the mode is on: `refresh()` does not call this otherwise, so a
 * train-only phone's poll is the one request it always was.
 */
export async function fetchModeDisruptions(mode) {
  const body = await ask(mode, '/disruptions', { timeout: 12_000 });
  if (!Array.isArray(body?.disruptions)) throw new Error(`${couldntReach()} for the ${mode.labels.many}.`);
  return body.disruptions;
}

/** The departures at a stop, both sides. */
export async function fetchModeBoard(mode, stopIds, { signal = null } = {}) {
  const ids = [...new Set((stopIds ?? []).map(Number).filter((n) => n > 0))].slice(0, 4);
  const body = await ask(mode, `/departures?stops=${ids.join(',')}`, { signal });
  return { departures: body?.departures ?? [], fetchedAt: body?.fetchedAt ?? null, firstAt: body?.firstAt ?? null };
}

/** One vehicle's stops, with a time against each. */
export async function fetchModeRun(mode, runRef, { signal = null } = {}) {
  const body = await ask(mode, `/run?ref=${encodeURIComponent(String(runRef))}`, { signal });
  return body?.run ?? null;
}

/** Is this a run a page can draw: one with stops in it? Null and an empty pattern are not. */
export const readableRun = (run) => Boolean(run) && Array.isArray(run.stops) && run.stops.length > 0;

/** Where the vehicles on one route are. Empty, never an error, when the feed is down. */
export async function fetchModeVehicles(mode, routeId) {
  try {
    const body = await ask(mode, `/vehicles?line=${Number(routeId)}`, { timeout: 8_000 });
    // NULL IS "WE DO NOT KNOW", an empty list is "none are running". A feed
    // that is down or not configured must not read as an empty line.
    if (!body || body.configured === false || !Array.isArray(body.vehicles)) return null;
    return body.vehicles;
  } catch {
    return null;
  }
}

/** "Trams" for a heading: the mode's plural, capitalised. */
export const pluralOf = (mode) => capital(mode.labels.many);
